import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Form, 
  Input, 
  Button, 
  message, 
  Tabs, 
  Table, 
  Space, 
  Typography, 
  Divider,
  Modal,
  Tag,
  Tooltip,
  Alert
} from 'antd';
import {
  UserOutlined,
  LockOutlined,
  SafetyOutlined,
  LogoutOutlined,
  EditOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  TeamOutlined,
  CalendarOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { getRoleDisplayName } from '../../utils/rolePermissions';
import axios from 'axios';
import { formatTimestamp, getRelativeTime, safeFormatTimestamp, safeGetRelativeTime } from '../../utils/timezone';
import logger from '../../services/logger';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const Profile = () => {
  const { user, logout } = useAuth();
  const isCustomer = user?.role?.name === 'customer';
  const [profile, setProfile] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [linkedCustomers, setLinkedCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profileForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [passwordRequirements, setPasswordRequirements] = useState(null);
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);
  const [profileSubmitting, setProfileSubmitting] = useState(false);

  useEffect(() => {
    fetchProfile();
    fetchSessions();
    fetchPasswordRequirements();
    if (isCustomer) {
      fetchLinkedCustomers();
    }
  }, []);

  const fetchLinkedCustomers = async () => {
    try {
      const response = await axios.get('/api/v1/profile/customers');
      setLinkedCustomers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      logger.error('Bağlı müşteriler yüklenirken hata:', error);
      setLinkedCustomers([]);
    }
  };

  const fetchProfile = async () => {
    logger.debug('🔵 fetchProfile called');
    try {
      logger.debug('🔵 Making GET request to /api/v1/profile');
      const response = await axios.get('/api/v1/profile');
      logger.info('✅ Profile fetched successfully:', response.data);
      setProfile(response.data);
      profileForm.setFieldsValue({
        ...response.data,
        role: getRoleDisplayName(response.data.role) || response.data.role
      });
    } catch (error) {
      logger.error('❌ Profile fetch error:', error);
      logger.error('❌ Error response:', error.response);
      message.error('Profil bilgileri yüklenirken hata oluştu');
    }
  };

  const fetchSessions = async () => {
    logger.debug('🔵 fetchSessions called');
    try {
      logger.debug('🔵 Making GET request to /api/v1/profile/sessions');
      const response = await axios.get('/api/v1/profile/sessions');
      logger.info('✅ Sessions fetched successfully:', response.data);
      logger.info('✅ Sessions count:', response.data.sessions?.length);
      logger.info('✅ Active sessions:', response.data.sessions?.filter(s => s.is_active).length);
      setSessions(response.data.sessions);
    } catch (error) {
      logger.error('❌ Sessions fetch error:', error);
      logger.error('❌ Error response:', error.response);
      message.error('Oturum bilgileri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const fetchPasswordRequirements = async () => {
    try {
      const response = await axios.get('/api/v1/profile/password-requirements');
      setPasswordRequirements(response.data);
    } catch (error) {
      logger.error('Şifre gereksinimleri yüklenirken hata oluştu:', error);
    }
  };

  const handleProfileUpdate = async (values) => {
    logger.debug('🔵 handleProfileUpdate called with values:', values);
    try {
      setProfileSubmitting(true);
      logger.debug('🔵 Making PUT request to /api/v1/profile');
      const response = await axios.put('/api/v1/profile', values);
      logger.info('✅ Profile update successful:', response.data);
      message.success('Profil bilgileri başarıyla güncellendi');
      fetchProfile();
    } catch (error) {
      logger.error('❌ Profile update error:', error);
      logger.error('❌ Error response:', error.response);
      
      let errorMessage = 'Profil güncellenirken hata oluştu';
      
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 400) {
        errorMessage = 'Geçersiz veri. Lütfen tüm alanları doğru şekilde doldurun.';
      } else if (error.response?.status === 409) {
        errorMessage = 'Bu e-posta adresi zaten kullanılıyor.';
      } else if (error.response?.status === 403) {
        errorMessage = 'Bu işlem için yetkiniz bulunmuyor.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Oturum süreniz dolmuş. Lütfen tekrar giriş yapın.';
      }
      
      message.error(errorMessage);
    } finally {
      setProfileSubmitting(false);
    }
  };

  const handlePasswordChange = async (values) => {
    logger.debug('🔵 handlePasswordChange called with values:', values);
    try {
      setPasswordSubmitting(true);

      if (values.new_password !== values.confirm_password) {
        logger.error('❌ Passwords do not match');
        message.error('Yeni şifreler eşleşmiyor');
        return;
      }

      logger.debug('🔵 Making POST request to /api/v1/profile/change-password');
      const response = await axios.post('/api/v1/profile/change-password', {
        current_password: values.current_password,
        new_password: values.new_password,
        confirm_password: values.confirm_password
      });
      logger.info('✅ Password change successful:', response.data);
      
      message.success('Şifre başarıyla değiştirildi');
      setPasswordModalVisible(false);
      passwordForm.resetFields();
      
      // Uyarıları göster
      if (response.data.warnings && response.data.warnings.length > 0) {
        response.data.warnings.forEach(warning => {
          message.warning(warning);
        });
      }
    } catch (error) {
      logger.error('Password change error:', error);
      
      let errorMessage = 'Şifre değiştirilirken hata oluştu';
      
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 400) {
        errorMessage = 'Geçersiz veri. Lütfen tüm alanları doğru şekilde doldurun.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Mevcut şifre yanlış.';
      } else if (error.response?.status === 403) {
        errorMessage = 'Bu işlem için yetkiniz bulunmuyor.';
      }
      
      message.error(errorMessage);
    } finally {
      setPasswordSubmitting(false);
    }
  };

  const handleTerminateSession = async (sessionId) => {
    logger.debug('🔵 handleTerminateSession called with sessionId:', sessionId);

    // Bu oturum aktif oturum mu kontrol et
    const session = sessions.find(s => s.id === sessionId);
    const isCurrentSession = session?.is_current;

    logger.debug('🔵 Is current session?', isCurrentSession);

    if (isCurrentSession) {
      // Kendi oturumunu sonlandırıyor, onay iste
      Modal.confirm({
        title: 'Kendi oturumunuzu sonlandırmak istediğinizden emin misiniz?',
        content: 'Bu işlem çıkış yapmanıza neden olacaktır.',
        onOk: async () => {
          try {
            logger.debug('🔵 Making DELETE request to /api/v1/profile/sessions/' + sessionId);
            await axios.delete(`/api/v1/profile/sessions/${sessionId}`);
            logger.info('✅ Current session terminated successfully');
            message.success('Oturum sonlandırıldı. Çıkış yapılıyor...', 2);

            // 1 saniye bekle ve logout yap
            setTimeout(() => {
              logout();
            }, 1000);
          } catch (error) {
            logger.error('❌ Session termination error:', error);
            message.error('Oturum sonlandırılırken hata oluştu');
          }
        }
      });
    } else {
      // Başka bir oturumu sonlandırıyor
      try {
        logger.debug('🔵 Making DELETE request to /api/v1/profile/sessions/' + sessionId);
        const response = await axios.delete(`/api/v1/profile/sessions/${sessionId}`);
        logger.info('✅ Session terminated successfully:', response.data);
        message.success('Oturum sonlandırıldı');
        fetchSessions();
      } catch (error) {
        logger.error('❌ Session termination error:', error);
        logger.error('❌ Error response:', error.response);
        logger.error('❌ Error response data:', error.response?.data);

        let errorMessage = 'Oturum sonlandırılırken hata oluştu';

        if (error.response?.data) {
          const data = error.response.data;
          // detail string mi object mi kontrol et
          if (typeof data.detail === 'string') {
            errorMessage = `Hata: ${data.detail}`;
          } else if (typeof data.detail === 'object') {
            errorMessage = `Hata: ${JSON.stringify(data.detail)}`;
          } else if (data.message) {
            errorMessage = `Hata: ${data.message}`;
          } else {
            errorMessage = `HTTP ${error.response.status} hatası: ${JSON.stringify(data)}`;
          }
        } else if (error.response?.status) {
          errorMessage = `HTTP ${error.response.status} hatası oluştu`;
        } else if (error.message) {
          errorMessage = `Hata: ${error.message}`;
        }

        message.error(errorMessage, 8);
      }
    }
  };

  const handleTerminateAllSessions = async () => {
    logger.debug('🔵 handleTerminateAllSessions called');
    Modal.confirm({
      title: 'Tüm oturumları sonlandırmak istediğinizden emin misiniz?',
      content: 'Bu işlem tüm cihazlardan (bu cihaz dahil) çıkış yapmanıza neden olacaktır.',
      onOk: async () => {
        logger.debug('🔵 User confirmed - Making DELETE request to /api/v1/profile/sessions/all');
        try {
          const response = await axios.delete('/api/v1/profile/sessions/all');
          logger.info('✅ All sessions terminated successfully:', response.data);
          message.success(`Tüm oturumlar sonlandırıldı (${response.data.terminated_count || 0} adet). Çıkış yapılıyor...`, 3);

          // 1 saniye bekle ve logout yap
          setTimeout(() => {
            logout();
          }, 1000);
        } catch (error) {
          logger.error('❌ All sessions termination error:', error);
          logger.error('❌ Error response:', error.response);
          logger.error('❌ Error response data:', error.response?.data);

          let errorMessage = 'Oturumlar sonlandırılırken hata oluştu';

          if (error.response?.data) {
            const data = error.response.data;
            // detail string mi object mi kontrol et
            if (typeof data.detail === 'string') {
              errorMessage = `Hata: ${data.detail}`;
            } else if (typeof data.detail === 'object') {
              errorMessage = `Hata: ${JSON.stringify(data.detail)}`;
            } else if (data.message) {
              errorMessage = `Hata: ${data.message}`;
            } else {
              errorMessage = `HTTP ${error.response.status} hatası: ${JSON.stringify(data)}`;
            }
          } else if (error.response?.status) {
            errorMessage = `HTTP ${error.response.status} hatası oluştu`;
          } else if (error.message) {
            errorMessage = `Hata: ${error.message}`;
          }

          message.error(errorMessage, 8);
        }
      }
    });
  };

  const sessionColumns = [
    {
      title: 'IP Adresi',
      dataIndex: 'ip_address',
      key: 'ip_address',
      render: (ip) => ip || '-'
    },
    {
      title: 'Tarayıcı',
      dataIndex: 'user_agent',
      key: 'user_agent',
      render: (userAgent) => {
        if (!userAgent) return '-';
        // Tarayıcı bilgisini kısalt
        const browser = userAgent.includes('Chrome') ? 'Chrome' :
                       userAgent.includes('Firefox') ? 'Firefox' :
                       userAgent.includes('Safari') ? 'Safari' :
                       userAgent.includes('Edge') ? 'Edge' : 'Diğer';
        return (
          <Tooltip title={userAgent}>
            <Text>{browser}</Text>
          </Tooltip>
        );
      }
    },
    {
      title: 'Giriş Zamanı',
      dataIndex: 'login_time',
      key: 'login_time',
      render: (date) => safeFormatTimestamp(date),
      sorter: (a, b) => new Date(a.login_time || 0) - new Date(b.login_time || 0)
    },
    {
      title: 'Son Aktivite',
      dataIndex: 'last_activity',
      key: 'last_activity',
      render: (date) => safeGetRelativeTime(date),
      sorter: (a, b) => new Date(a.last_activity || 0) - new Date(b.last_activity || 0)
    },
    {
      title: 'Durum',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (active) => (
        <Tag color={active ? 'green' : 'red'}>
          {active ? 'Aktif' : 'Pasif'}
        </Tag>
      )
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title={record.is_active ? "Oturumu Sonlandır" : "Oturum zaten pasif"}>
            <Button
              type="text"
              danger={record.is_active}
              icon={<LogoutOutlined />}
              onClick={() => handleTerminateSession(record.id)}
              size="small"
              disabled={!record.is_active}
            />
          </Tooltip>
        </Space>
      )
    }
  ];

  if (loading) {
    return <div>Yükleniyor...</div>;
  }

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>
          <UserOutlined /> Profil Yönetimi
        </Title>
      </div>

      <Tabs defaultActiveKey="profile">
        <TabPane tab="Profil Bilgileri" key="profile">
          {isCustomer && (
            <Card
              size="small"
              style={{ marginBottom: 16 }}
              title={<Space><TeamOutlined /><span>Bağlı Olduğunuz Müşteri</span></Space>}
            >
              {linkedCustomers.length === 0 ? (
                <Text type="secondary">Henüz bir müşteriye atanmadınız.</Text>
              ) : (
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  {linkedCustomers.map(c => (
                    <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Space>
                        <Text strong>{c.name}</Text>
                        {c.contact_email && <Text type="secondary" style={{ fontSize: 12 }}>· {c.contact_email}</Text>}
                        {c.contact_phone && <Text type="secondary" style={{ fontSize: 12 }}>· {c.contact_phone}</Text>}
                      </Space>
                    </div>
                  ))}
                </Space>
              )}
            </Card>
          )}
          <Card>
            <Form
              form={profileForm}
              layout="vertical"
              onFinish={handleProfileUpdate}
            >
              <Form.Item
                name="username"
                label="Kullanıcı Adı"
              >
                <Input disabled prefix={<UserOutlined />} />
              </Form.Item>

              <Form.Item
                name="full_name"
                label="Ad Soyad"
                rules={[
                  { required: true, message: 'Ad soyad gerekli' },
                  { min: 2, message: 'Ad soyad en az 2 karakter olmalıdır' },
                  { max: 255, message: 'Ad soyad en fazla 255 karakter olabilir' },
                  {
                    pattern: /^[a-zA-ZğüşıöçĞÜŞİÖÇ\s.'-]+$/,
                    message: 'Ad soyad sadece harf, boşluk, nokta, tire ve kesme işareti içerebilir'
                  }
                ]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                name="email"
                label="E-posta"
                rules={[
                  { required: true, message: 'E-posta gerekli' },
                  { type: 'email', message: 'Geçerli bir e-posta adresi girin' },
                  { max: 255, message: 'E-posta en fazla 255 karakter olabilir' },
                  { 
                    pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                    message: 'Geçerli bir e-posta adresi formatı girin'
                  }
                ]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                name="role"
                label="Rol"
              >
                <Input disabled />
              </Form.Item>

              {profile && (
                <Divider style={{ margin: '12px 0 16px' }}>Hesap Bilgileri</Divider>
              )}
              {profile && (
                <Space direction="vertical" size="small" style={{ width: '100%', marginBottom: 16 }}>
                  <Space size="small">
                    <CalendarOutlined style={{ color: '#8c8c8c' }} />
                    <Text type="secondary">Hesap Oluşturma:</Text>
                    <Text>{profile.created_at ? safeFormatTimestamp(profile.created_at) : '-'}</Text>
                  </Space>
                  <Space size="small">
                    <ClockCircleOutlined style={{ color: '#8c8c8c' }} />
                    <Text type="secondary">Son Giriş:</Text>
                    <Text>{profile.last_login ? safeFormatTimestamp(profile.last_login) : 'Kayıt yok'}</Text>
                  </Space>
                </Space>
              )}

              <Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit" icon={<EditOutlined />} loading={profileSubmitting}>
                    Güncelle
                  </Button>
                  <Button 
                    type="default" 
                    icon={<LockOutlined />}
                    onClick={() => setPasswordModalVisible(true)}
                    disabled={profileSubmitting}
                  >
                    Şifre Değiştir
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Card>
        </TabPane>

        <TabPane tab="Güvenlik" key="security">
          <Card>
            <Title level={4}>
              <SafetyOutlined /> Oturum Yönetimi
            </Title>
            
            <Alert
              message="Güvenlik Uyarısı"
              description="Şüpheli aktivite fark ederseniz tüm oturumları sonlandırabilirsiniz."
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />

            <div style={{ marginBottom: 16 }}>
              <Button
                type="primary"
                danger
                icon={<LogoutOutlined />}
                onClick={handleTerminateAllSessions}
              >
                Tüm Oturumları Sonlandır
              </Button>
            </div>

            <Table
              columns={sessionColumns}
              dataSource={sessions}
              rowKey="id"
              pagination={false}
              size="small"
            />
          </Card>
        </TabPane>
      </Tabs>

      {/* Şifre Değiştirme Modal */}
      <Modal
        title="Şifre Değiştir"
        open={passwordModalVisible}
        onCancel={() => {
          if (!passwordSubmitting) {
            setPasswordModalVisible(false);
            passwordForm.resetFields();
          }
        }}
        footer={null}
        closable={!passwordSubmitting}
        maskClosable={!passwordSubmitting}
      >
        <Form
          form={passwordForm}
          layout="vertical"
          onFinish={handlePasswordChange}
        >
          <Form.Item
            name="current_password"
            label="Mevcut Şifre"
            rules={[{ required: true, message: 'Mevcut şifre gerekli' }]}
          >
            <Input.Password prefix={<LockOutlined />} />
          </Form.Item>

          <Form.Item
            name="new_password"
            label="Yeni Şifre"
            rules={[
              { required: true, message: 'Yeni şifre gerekli' },
              { min: passwordRequirements?.min_length || 8, message: `Şifre en az ${passwordRequirements?.min_length || 8} karakter olmalıdır` },
              { 
                pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>])[A-Za-z\d!@#$%^&*(),.?":{}|<>]/,
                message: 'Şifre en az bir büyük harf, bir küçük harf, bir sayı ve bir özel karakter içermelidir'
              }
            ]}
            help={
              passwordRequirements && (
                <div style={{ fontSize: '12px', color: '#666' }}>
                  <div>Şifre gereksinimleri:</div>
                  <div>• En az {passwordRequirements.min_length} karakter</div>
                  <div>• En az bir büyük harf</div>
                  <div>• En az bir küçük harf</div>
                  <div>• En az bir sayı</div>
                  <div>• En az bir özel karakter (!@#$%^&*(),.?":{}|&lt;&gt;)</div>
                  <div>• Kullanıcı adını içermemeli</div>
                </div>
              )
            }
          >
            <Input.Password prefix={<LockOutlined />} />
          </Form.Item>

          <Form.Item
            name="confirm_password"
            label="Yeni Şifre (Tekrar)"
            rules={[
              { required: true, message: 'Şifre tekrarı gerekli' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('new_password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Şifreler eşleşmiyor'));
                },
              }),
            ]}
          >
            <Input.Password prefix={<LockOutlined />} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={passwordSubmitting}>
                Şifre Değiştir
              </Button>
              <Button onClick={() => {
                setPasswordModalVisible(false);
                passwordForm.resetFields();
              }} disabled={passwordSubmitting}>
                İptal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Profile;
